import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import billingService from '../../services/billingService';
import toast from 'react-hot-toast';

export const fetchInvoices = createAsyncThunk(
  'billing/fetchInvoices',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await billingService.getInvoices(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch invoices');
    }
  }
);

export const fetchInvoice = createAsyncThunk(
  'billing/fetchInvoice',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await billingService.getInvoice(id);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch invoice');
    }
  }
);

export const fetchPayments = createAsyncThunk(
  'billing/fetchPayments',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await billingService.getPayments(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch payments');
    }
  }
);

export const fetchRateCards = createAsyncThunk(
  'billing/fetchRateCards',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await billingService.getRateCards(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch rate cards');
    }
  }
);

export const createRateCard = createAsyncThunk(
  'billing/createRateCard',
  async (rateCardData, { rejectWithValue }) => {
    try {
      const { data } = await billingService.createRateCard(rateCardData);
      toast.success('Rate card created');
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to create rate card');
    }
  }
);

export const fetchPayouts = createAsyncThunk(
  'billing/fetchPayouts',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await billingService.getPayouts(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch payouts');
    }
  }
);

const initialState = {
  invoices: [],
  currentInvoice: null,
  invoiceTotalCount: 0,
  invoiceLoading: false,
  payments: [],
  paymentTotalCount: 0,
  paymentLoading: false,
  rateCards: [],
  rateCardLoading: false,
  payouts: [],
  payoutTotalCount: 0,
  payoutLoading: false,
  stats: null,
  error: null,
};

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    clearCurrentInvoice: (state) => {
      state.currentInvoice = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvoices.pending, (state) => {
        state.invoiceLoading = true;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.invoiceLoading = false;
        state.invoices = (() => { const _d = action.payload; return Array.isArray(_d?.results) ? _d.results : Array.isArray(_d?.items) ? _d.items : Array.isArray(_d) ? _d : []; })();
        state.invoiceTotalCount = action.payload.count || 0;
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.invoiceLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchInvoice.fulfilled, (state, action) => {
        state.currentInvoice = action.payload;
      })
      .addCase(fetchPayments.pending, (state) => {
        state.paymentLoading = true;
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.paymentLoading = false;
        state.payments = (() => { const _d = action.payload; return Array.isArray(_d?.results) ? _d.results : Array.isArray(_d?.items) ? _d.items : Array.isArray(_d) ? _d : []; })();
        state.paymentTotalCount = action.payload.count || 0;
      })
      .addCase(fetchPayments.rejected, (state) => {
        state.paymentLoading = false;
      })
      .addCase(fetchRateCards.pending, (state) => {
        state.rateCardLoading = true;
      })
      .addCase(fetchRateCards.fulfilled, (state, action) => {
        state.rateCardLoading = false;
        state.rateCards = (() => { const _d = action.payload; return Array.isArray(_d?.results) ? _d.results : Array.isArray(_d?.items) ? _d.items : Array.isArray(_d) ? _d : []; })();
      })
      .addCase(fetchRateCards.rejected, (state) => {
        state.rateCardLoading = false;
      })
      .addCase(createRateCard.fulfilled, (state, action) => {
        state.rateCards.unshift(action.payload);
      })
      .addCase(fetchPayouts.pending, (state) => {
        state.payoutLoading = true;
      })
      .addCase(fetchPayouts.fulfilled, (state, action) => {
        state.payoutLoading = false;
        state.payouts = (() => { const _d = action.payload; return Array.isArray(_d?.results) ? _d.results : Array.isArray(_d?.items) ? _d.items : Array.isArray(_d) ? _d : []; })();
        state.payoutTotalCount = action.payload.count || 0;
      })
      .addCase(fetchPayouts.rejected, (state) => {
        state.payoutLoading = false;
      });
  },
});

export const { clearCurrentInvoice, clearError } = billingSlice.actions;
export default billingSlice.reducer;
